/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import validatorStyle from "./validatorStyle";
import { ValidatorType } from "../../types/ValidatorType";
import { Autocomplete, AutocompleteChangeReason, AutocompleteProps, TextField } from "@mui/material";
import { liteClient } from "algoliasearch/lite";
import { ALGOLIA_APPLICATION_ID, ALGOLIA_ORGANIZATION_INDEX, ALGOLIA_SEARCH_API_KEY } from "../../config/_entrypoint";
import { Hit } from "@algolia/client-search";
import { Organization } from "../../types/Organization";
import { FormChangeEvent, FormChangeEventHandler } from "../../types/UseForm";
import _ from "lodash";

type OrganizationHit = Hit<Organization>;

type ValidatorOrganizationAutocompleteProps = {
  label: string;
  registerValidators?: Function;
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<string>;
  helperText?: string;
  isHighlighted?: boolean;
  error?: boolean;
  name: string;
  emptyText?: string;
  noOptionsText?: string;
  organizationType?: string | null;
  onChange: FormChangeEventHandler<HTMLInputElement>;
  value?: string;
} & Omit<AutocompleteProps<OrganizationHit, false, false, false>, "onChange" | "renderInput" | "options" | "value">;

const ValidatorOrganizationAutocomplete = ({
  label,
  registerValidators = () => {},
  validators = [],
  dependentValidationFields = [],
  helperText = "",
  isHighlighted = false,
  error = false,
  disabled,
  value,
  name,
  emptyText = "Bitte fangen Sie an zu tippen um zu suchen",
  noOptionsText = "Nicht gefunden",
  onChange,
  organizationType = null,
  ...rest
}: ValidatorOrganizationAutocompleteProps) => {
  const [options, setOptions] = useState<OrganizationHit[]>([]);
  const client = useMemo(() => liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY), []);
  const organizationId = value && value.split("/").pop();
  const objectId = organizationId ? `organization::${organizationId}` : null;

  const setValueFromOption = (option: OrganizationHit) => {
    const selectedOrganizationId = option.objectID.split("::").pop();

    onChange({
      target: {
        type: "text",
        name: name,
        value: `/organizations/${selectedOrganizationId}`,
      },
    } as FormChangeEvent);
  };

  const fetchOptions = useCallback(
    _.debounce((searchText: string) => {
      if (!searchText.trim()) {
        setOptions([]);
        return;
      }

      const filters = organizationType ? { filters: "type:" + organizationType } : {};

      client
        .searchForHits<OrganizationHit>({
          requests: [
            {
              indexName: ALGOLIA_ORGANIZATION_INDEX,
              query: searchText,
              ...filters,
            },
          ],
        })
        .then(({ results }) => {
          const hits = results[0]?.hits ?? [];
          const sortedHits = hits.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setOptions(sortedHits);
        })
        .catch((err) => console.error(err));
    }, 300),
    [client, organizationType]
  );

  useEffect(() => {
    if (organizationId) {
      client
        .searchForHits<OrganizationHit>({
          requests: [
            {
              indexName: ALGOLIA_ORGANIZATION_INDEX,
              query: "",
              filters: `objectID:"${objectId}"`,
            },
          ],
        })
        .then(({ results }) => {
          const hits = results[0]?.hits ?? [];
          if (hits.length > 0) {
            setOptions([hits[0]]);
            setValueFromOption(hits[0]);
          }
        })
        .catch((err) => console.error("Fehler beim Laden der initialen Option:", err));
    }
  }, [client, objectId, organizationId]);

  useEffect(() => {
    if (registerValidators && validators && !disabled) {
      registerValidators(name, validators, dependentValidationFields);
    }
    return () => {
      registerValidators && registerValidators(name, []);
    };
  }, [registerValidators, dependentValidationFields, name, disabled]);

  const handleAutoCompleteValueChange = (
    e: React.SyntheticEvent,
    value: OrganizationHit | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      onChange({
        target: {
          type: "text",
          name: name,
          value: null,
        },
      } as FormChangeEvent);
    }

    if (!value) {
      return;
    }

    setValueFromOption(value);
  };

  const handleInputChange = (e: React.SyntheticEvent, newInputValue: string) => {
    fetchOptions(newInputValue);
  };

  const selectedValue = _.find(options, (option) => option.objectID === objectId) || null;

  return (
    <FormControl
      fullWidth={true}
      margin={"none"}
      sx={isHighlighted && selectedValue === null ? validatorStyle.mandatory : undefined}
      error={error}
      disabled={disabled}
    >
      <Autocomplete
        {...rest}
        renderInput={(params) => <TextField {...params} label={label} />}
        options={options}
        value={selectedValue}
        onChange={handleAutoCompleteValueChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.name || ""}
        noOptionsText={objectId ? noOptionsText : emptyText}
        isOptionEqualToValue={(option, value) => option.objectID === value.objectID}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ValidatorOrganizationAutocomplete;
